// Tercera columna del explorer: la ficha del deudor. Encabezado, la fila de acciones y las cinco
// pestañas del mockup (Resumen · Créditos · Contactos · Compromisos · Pagos).
import { useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CreditCard, MessageSquare, Phone, Zap } from 'lucide-react';
import { Button, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { DrawerEmpty, FileDrawerTabs } from '@/shared/patterns/FileDrawer';
import { TierPill } from '@/shared/patterns/Pills';
import {
    CONTACTS_SHOWN,
    NO_VALUE,
    arrearsLabel,
    arrearsTone,
    dailySuggestion,
    formatFixtureDay,
    formatMoneyCents,
    initialsOf,
    promiseCell,
    promisesSummary,
} from '../logic';
import {
    CONTACT_CHANNEL_LABEL,
    CONTACT_OUTCOME_LABEL,
    CUSTOMER_TABS,
    PAYMENT_CHANNEL_LABEL,
    PROMISE_STATUS_LABEL,
    type CollectorCustomerRow,
    type CustomerTabKey,
    type PromiseStatus,
} from '../types';
import { ToneChip } from './ToneChip';

const SECTION_TITLE = 'text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';

const PROMISE_STATUS_TONE: Record<PromiseStatus, 'info' | 'success' | 'danger'> = {
    active: 'info',
    kept: 'success',
    broken: 'danger',
};

/** Los avisos de las tres acciones que todavía no hacen nada (`showModal` del mockup). */
const ACTION_NOTICES = {
    call: {
        title: 'Llamar',
        body: 'Se marca desde el contact center. Registra el resultado al colgar.',
    },
    sms: {
        title: 'Enviar SMS o WhatsApp',
        body: 'Selecciona plantilla del ciclo por día de mora.',
    },
    oxxo: {
        title: 'Referencia OXXO',
        body: 'Se genera y envía referencia por SMS o chat.',
    },
} as const;

type ActionNoticeKey = keyof typeof ACTION_NOTICES;

/** Fila rótulo · valor del mockup (`.info-row`). */
function InfoRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="grid grid-cols-[100px_1fr] gap-1.5 py-[3px] text-[11.5px]">
            <span className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span className="break-words">{value}</span>
        </div>
    );
}

export interface CustomerDetailProps {
    customer: CollectorCustomerRow | null;
}

export function CustomerDetail({ customer }: CustomerDetailProps) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<CustomerTabKey>('summary');
    const [notice, setNotice] = useState<ActionNoticeKey | null>(null);

    if (customer === null) {
        return (
            <MasterDetailEmpty
                title="Elige un cliente"
                sub="La ficha muestra su cobranza, su perfil y la sugerencia del día."
            />
        );
    }

    const openAgenda = () => {
        void navigate({ to: '/collections/$section', params: { section: 'agenda' } });
    };

    const promise = promiseCell(customer);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-none border-b border-border px-3.5 py-3">
                <div className="flex items-start gap-3">
                    <span
                        aria-hidden
                        className="inline-flex size-[34px] flex-none items-center justify-center rounded-full bg-primary/15 text-[12px] font-bold text-primary"
                    >
                        {initialsOf(customer.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-semibold leading-tight">
                            {customer.name}
                        </div>
                        <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                            {/* eslint-disable-next-line no-restricted-syntax -- folio de negocio: es con lo que el cobrador nombra al cliente por teléfono */}
                            {customer.id} · {customer.phone ?? NO_VALUE}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                            <TierPill level={customer.tier} />
                            <ToneChip tone={arrearsTone(customer.worstArrearsDays)}>
                                {arrearsLabel(customer.worstArrearsDays)}
                            </ToneChip>
                            {promise === null ? null : (
                                <ToneChip tone={promise.tone}>{promise.label}</ToneChip>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-none flex-wrap gap-1.5 px-3.5 py-2">
                <Button
                    size="sm"
                    leftIcon={<Zap className="size-2.5" aria-hidden />}
                    onClick={openAgenda}
                >
                    Abrir en Agenda
                </Button>
                <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Phone className="size-2.5" aria-hidden />}
                    onClick={() => {
                        setNotice('call');
                    }}
                >
                    Llamar
                </Button>
                <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<MessageSquare className="size-2.5" aria-hidden />}
                    onClick={() => {
                        setNotice('sms');
                    }}
                >
                    SMS
                </Button>
                <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<CreditCard className="size-2.5" aria-hidden />}
                    onClick={() => {
                        setNotice('oxxo');
                    }}
                >
                    Ref. OXXO
                </Button>
            </div>

            <FileDrawerTabs
                tabs={CUSTOMER_TABS}
                activeTab={activeTab}
                onChange={(key) => {
                    setActiveTab(key as CustomerTabKey);
                }}
            />

            <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3 text-[11.5px]">
                {activeTab === 'summary' ? <SummaryPanel customer={customer} /> : null}
                {activeTab === 'credits' ? (
                    <CreditsPanel customer={customer} onOpenAgenda={openAgenda} />
                ) : null}
                {activeTab === 'contacts' ? <ContactsPanel customer={customer} /> : null}
                {activeTab === 'promises' ? <PromisesPanel customer={customer} /> : null}
                {activeTab === 'payments' ? <PaymentsPanel customer={customer} /> : null}
            </div>

            <Modal
                isOpen={notice !== null}
                onClose={() => {
                    setNotice(null);
                }}
                title={notice === null ? '' : ACTION_NOTICES[notice].title}
                size="sm"
            >
                <p className="text-[13px] leading-relaxed">
                    {notice === null ? '' : ACTION_NOTICES[notice].body}
                </p>
                <div className="mt-4 flex justify-end">
                    <Button
                        size="sm"
                        onClick={() => {
                            setNotice(null);
                        }}
                    >
                        Entendido
                    </Button>
                </div>
            </Modal>
        </div>
    );
}

function SummaryPanel({ customer }: { customer: CollectorCustomerRow }) {
    const last = customer.lastContact;
    const lastLabel =
        last === null
            ? 'Sin contacto reciente'
            : `${CONTACT_CHANNEL_LABEL[last.channel]} · ${CONTACT_OUTCOME_LABEL[last.outcome]} · ${formatFixtureDay(last.at)}`;

    return (
        <>
            <div className={cn(SECTION_TITLE, 'mb-1')}>Cobranza</div>
            <InfoRow label="DPI mora" value={`${String(customer.worstArrearsDays)} días`} />
            <InfoRow
                label="Monto vencido"
                value={
                    <span className="font-semibold tabular-nums">
                        {formatMoneyCents(customer.totalArrearsCents)}
                    </span>
                }
            />
            <InfoRow label="Últ. contacto" value={lastLabel} />
            <InfoRow label="Intentos 48h" value={`${String(customer.attempts48h)} intentos`} />
            <InfoRow label="Compromisos" value={promisesSummary(customer)} />

            <div className={cn(SECTION_TITLE, 'mb-1 mt-3')}>Perfil</div>
            <InfoRow
                label="Ingreso mensual"
                value={
                    customer.monthlyIncomeCents === null
                        ? NO_VALUE
                        : formatMoneyCents(customer.monthlyIncomeCents)
                }
            />
            <InfoRow
                label="Score alt"
                value={customer.altScore === null ? NO_VALUE : String(customer.altScore)}
            />
            <InfoRow
                label="CURP"
                value={<span className="font-mono text-[10.5px]">{customer.curp ?? NO_VALUE}</span>}
            />

            <div className={cn(SECTION_TITLE, 'mb-1 mt-3')}>Sugerencia del día</div>
            <p className="rounded-md bg-info-soft p-2 text-[11.5px] leading-relaxed text-info">
                {dailySuggestion(customer)}
            </p>
        </>
    );
}

function CreditsPanel({
    customer,
    onOpenAgenda,
}: {
    customer: CollectorCustomerRow;
    onOpenAgenda: () => void;
}) {
    if (customer.credits.length === 0) {
        return <DrawerEmpty>Sin créditos asignados.</DrawerEmpty>;
    }
    return (
        <>
            {customer.credits.map((credit) => (
                <button
                    key={credit.id}
                    type="button"
                    onClick={onOpenAgenda}
                    className="mb-2 block w-full rounded-md border border-border p-2 text-left transition-colors hover:bg-accent/40"
                >
                    <div className="flex items-center justify-between">
                        {/* eslint-disable-next-line no-restricted-syntax -- folio del crédito: es con lo que se cruza el caso contra la Agenda */}
                        <span className="font-mono text-[12px] font-semibold">{credit.id}</span>
                        <ToneChip tone={arrearsTone(credit.arrearsDays)}>
                            {arrearsLabel(credit.arrearsDays)}
                        </ToneChip>
                    </div>
                    <div className="text-[10.5px] text-muted-foreground">
                        {credit.deviceBrand} {credit.deviceModel} · {credit.paidInstallments}/
                        {credit.totalInstallments} cuotas
                    </div>
                    <div className="mt-0.5 text-[10.5px]">
                        Mora:{' '}
                        <span className="font-semibold tabular-nums">
                            {formatMoneyCents(credit.arrearsCents)}
                        </span>
                    </div>
                </button>
            ))}
        </>
    );
}

function ContactsPanel({ customer }: { customer: CollectorCustomerRow }) {
    if (customer.contacts.length === 0) {
        return <DrawerEmpty>Sin historial.</DrawerEmpty>;
    }
    return (
        <>
            {customer.contacts.slice(0, CONTACTS_SHOWN).map((contact) => (
                <div
                    key={`${contact.creditId}-${contact.at}`}
                    className="border-b border-border/60 py-1.5"
                >
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-muted-foreground">
                            {formatFixtureDay(contact.at)} ·{' '}
                            {CONTACT_CHANNEL_LABEL[contact.channel]} ·{' '}
                            {CONTACT_OUTCOME_LABEL[contact.outcome]}
                        </span>
                        <span className="font-mono text-[9.5px] text-muted-foreground">
                            {/* eslint-disable-next-line no-restricted-syntax -- folio del crédito: dice a cuál de sus créditos fue ese contacto */}
                            {contact.creditId}
                        </span>
                    </div>
                    <div className="text-[11px]">
                        {contact.note === '' ? NO_VALUE : contact.note}
                    </div>
                </div>
            ))}
        </>
    );
}

function PromisesPanel({ customer }: { customer: CollectorCustomerRow }) {
    if (customer.promises.length === 0) {
        return <DrawerEmpty>Sin compromisos.</DrawerEmpty>;
    }
    return (
        <>
            {customer.promises.map((promise) => (
                <div key={promise.id} className="border-b border-border/60 py-1.5">
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[11px] font-semibold">
                            {/* eslint-disable-next-line no-restricted-syntax -- folio del compromiso: es lo que se cita al reclamarlo */}
                            {promise.id} · {formatFixtureDay(promise.day)}
                        </span>
                        <ToneChip tone={PROMISE_STATUS_TONE[promise.status]}>
                            {PROMISE_STATUS_LABEL[promise.status]}
                        </ToneChip>
                    </div>
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                        {formatMoneyCents(promise.amountCents)} ·{' '}
                        {PAYMENT_CHANNEL_LABEL[promise.channel]}
                    </div>
                </div>
            ))}
        </>
    );
}

function PaymentsPanel({ customer }: { customer: CollectorCustomerRow }) {
    if (customer.payments.length === 0) {
        return <DrawerEmpty>Sin pagos.</DrawerEmpty>;
    }
    return (
        <>
            {customer.payments.map((payment) => (
                <div key={payment.id} className="border-b border-border/60 py-1.5">
                    <div className="flex items-center justify-between gap-2">
                        {/* eslint-disable-next-line no-restricted-syntax -- folio del pago: es lo que el cliente tiene en su comprobante */}
                        <span className="font-mono text-[11px] font-semibold">{payment.id}</span>
                        <span className="font-semibold tabular-nums text-success">
                            {formatMoneyCents(payment.amountCents)}
                        </span>
                    </div>
                    <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                        {formatFixtureDay(payment.at)} · {PAYMENT_CHANNEL_LABEL[payment.channel]}
                    </div>
                </div>
            ))}
        </>
    );
}
